import { useEffect, useState } from 'react';
import { supabaseClient } from '../config/supabase';
import { loadModuleModes } from '../lib/breakerPanel';

// Breaker Panel G4: the ACTIVE company's breaker per module ({ [module_key]: { mode, ai_mode } }), shared by every screen
// through one in-flight read. The cache is keyed by company id (the read itself filters by auth_org_id) and is cleared
// when the company changes (clearModuleModesCache, called by the company switcher before its reload), so a person in
// several companies never sees one company's breakers on another's screens. Missing or unreadable reads as "unknown"
// (null): screens then show nothing extra rather than guessing. Call refreshModuleModes() after a switch changes.
let cache = null; // { orgId, modes }
let inflight = null;
const listeners = new Set();

function fetchModes(client) {
    if (!inflight) {
        inflight = loadModuleModes(client)
            .then((result) => {
                // A different company than the cached one: the old company's entry is dropped, never merged.
                cache = { orgId: result?.orgId || null, modes: result?.modes || {} };
                return cache.modes;
            })
            .catch(() => { cache = cache || { orgId: null, modes: {} }; return cache.modes; })
            .finally(() => { inflight = null; listeners.forEach((listener) => listener(cache ? cache.modes : null)); });
    }
    return inflight;
}

export function refreshModuleModes(client = supabaseClient) {
    cache = null;
    return fetchModes(client);
}

/** Forget every company's breakers (company switch, sign-out). */
export function clearModuleModesCache() {
    cache = null; inflight = null;
    listeners.forEach((listener) => listener(null));
}

/** The company id the cached breakers belong to (null when nothing is cached). */
export function cachedModuleModesOrgId() { return cache ? cache.orgId : null; }

/** For tests. */
export function resetModuleModesCache() { cache = null; inflight = null; }

export default function useModuleModes(client = supabaseClient) {
    const [modes, setModes] = useState(cache ? cache.modes : null);
    useEffect(() => {
        listeners.add(setModes);
        if (cache) setModes(cache.modes); else fetchModes(client);
        return () => { listeners.delete(setModes); };
    }, [client]);
    return modes;
}

/** The mode of one module: 'test' | 'live' | null while unknown. */
export function useModuleMode(moduleKey, client = supabaseClient) {
    const modes = useModuleModes(client);
    return modes && moduleKey ? (modes[moduleKey]?.mode || null) : null;
}
